import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';

import { AlertsService } from './alerts.service';

@Injectable({
  providedIn: 'root',
})
export class FileCompressionService {
  readonly #alerts = inject(AlertsService);
  readonly #transloco = inject(TranslocoService);

  /**
   * Compresses image files. HEIC inputs are converted to JPEG first.
   */
  async compressFiles(
    files: File[],
    maxSizeMB = 15,
    quality = 0.7,
  ): Promise<File[]> {
    return Promise.all(files.map((file) => this.#processFile(file, maxSizeMB, quality)));
  }

  async #processFile(
    file: File,
    maxSizeMB: number,
    quality: number,
  ): Promise<File> {
    let imageFile = file;

    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      try {
        const convertedBlob = (await heic2any({
          blob: file,
          toType: 'image/jpeg',
        })) as Blob;

        imageFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$/i, '.jpg'),
          { type: 'image/jpeg' },
        );
      } catch {
        throw new Error(`Failed to convert HEIC file: ${file.name}`);
      }
    }

    try {
      const compressedFile = await imageCompression(imageFile, {
        maxSizeMB,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: quality,
      });

      if (compressedFile.size > maxSizeMB * 1024 * 1024) {
        void this.#alerts.error(
          this.#transloco.translate('shared|files|error|file-too-large'),
        );
        throw new Error(
          `Compressed file exceeds ${maxSizeMB}MB size limit: ${file.name}`,
        );
      }

      return compressedFile;
    } catch (error) {
      if (error instanceof Error && error.message.includes('size limit')) {
        throw error;
      }
      throw new Error(
        error instanceof Error
          ? `Compression failed for ${file.name}: ${error.message}`
          : `Compression failed for ${file.name}`,
      );
    }
  }
}
